/**
 * 63.14 step 6's start / DIRECTION §0.11 step B (2): settlements as module layouts on scored sites (ARCHITECTURE §5.22
 * "Settlements per seed"; DECISIONS 201 (4): hand-made layouts first; 205: flat sites first). **The planet's sites, from
 * the planet alone** (§0.11 (1): a pure function, cached per planet; nothing in the match state but the result): a keyed
 * scan of the planet's surface scored by the site score (core/siteScore.ts, flatness first) for a settlement's footprint,
 * the best SETTLEMENT.perPlanet of them spaced SETTLEMENT.spacingPeople apart; each a **lane** at a keyed yaw with
 * SETTLEMENT.lotsMin–lotsMax **lots** either side of it, each lot one of the ten hand-made layouts (core/modules.ts
 * TEAM_LAYOUTS, by builder index) facing the lane. Lane C's layout pass (step A, `placeOpenObjectives`) reads the list to
 * pick the match's objectives among them; `withSettlements` lays the lots of the settlements the board bounds as neutral
 * `house` structures at setup (behind `GameConfig.settlements`), which the setup's passes then settle (settleBuildings),
 * terrace (terraceBuilds, 2b), seat and stand like any building sized by function — drawn by their builder's model, or
 * its layout from the kit with modular buildings on, walked, fought over and broken as any modular building is. Off: no
 * structure, every byte as before. Not here: the far impostor and the held band at planet scale (§5.22 step 6's rest).
 */
import type { GameState, PlanetState, StructureState, Vec3 } from '../types';
import { SeededRandom } from './random';
import { terraceSiteScore } from './siteScore';
import { buildingsByFunction, BUILDERS } from './buildingScale';
import { structureHealth } from './body';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { structureFrame } from './hits';
import { dacos, dcos, dsin } from './dmath';
import { vecNormalize, vecDot } from '../math';

export const SETTLEMENT = {
  /** How many settlement sites a planet offers at least, and at most (the best of the scan, spaced): by its area, one per (2 × spacing)² between these. */
  perPlanetMin: 12,
  perPlanetMax: 48,
  /** The scan: this many keyed directions over the sphere. */
  candidates: 2000,
  /** Sites stand at least this far apart (people). */
  spacingPeople: 300,
  /** The footprint a site is scored for (half extent, people): the lane and its lots. */
  sitePeople: 16,
  /** Lots a settlement: between these. */
  lotsMin: 3,
  lotsMax: 5,
  /** The lane's half width (people): the lots' fronts stand this far from its line. */
  lanePeople: 4,
  /** The lots' pitch along the lane (people): the widest layout and a walk between. */
  pitchPeople: 14,
  /** The lots' centres stand this far from the lane's line (people): the lane's half width and half the deepest layout. */
  setbackPeople: 10,
  /** How many settlements a match lays at most (the board's region first, the best first). */
  maxPlaced: 6,
  /** With a board: a settlement lies within this share of the bases' separation of each base (the lens between them). */
  boardReach: 1.1,
} as const;

/** One lot: the layout (its builder index, core/modules.ts teamLayout), where it stands, its yaw (its door toward the lane). */
export interface SettlementLot { builder: number; dir: Vec3; yaw: number }
/** A settlement site: its centre, its lane's yaw, its site score, its lots. */
export interface SettlementSite { id: number; dir: Vec3; yaw: number; score: number; lots: SettlementLot[] }

/** Per generated heightmap (the planet as made: `initialHeightmap` once anything has edited it), by person scale. */
const cache = new WeakMap<Float32Array, Map<string, readonly SettlementSite[]>>();

/** The angle between two unit directions. */
const arc = (a: Vec3, b: Vec3) => dacos(Math.max(-1, Math.min(1, vecDot(a, b))));

/**
 * The planet's settlement sites, from the planet alone (its id keys the scan's phase and each site's lots; the ground
 * scores them): pure, cached per planet object and person scale. Empty where the buildings are not sized by function.
 */
export function planetSettlements(state: Pick<GameState, 'planets' | 'config'>, planetIndex: number): readonly SettlementSite[] {
  const current = state.planets[planetIndex];
  if (!current || current.type !== 'planet' || !buildingsByFunction(state.config)) return [];
  const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k;
  // the planet as generated: its first heightmap, before any crater, dig, terrace or stand — so the sites are the same in every match on it, whenever asked
  const generated = current.initialHeightmap ?? current.heightmap;
  const planet: PlanetState = { ...current, heightmap: generated, initialHeightmap: undefined, digs: undefined, craterlets: undefined, terraces: undefined, stands: undefined, standCells: undefined };
  const scoreState = { planets: state.planets.map((p, i) => (i === planetIndex ? planet : p)), config: state.config };
  const key = `${ps.k}|${ps.fineAmplitude}|${ps.fineWavelength}`;
  let byKey = cache.get(generated);
  if (!byKey) { byKey = new Map(); cache.set(generated, byKey); }
  const hit = byKey.get(key);
  if (hit) return hit;
  const score = terraceSiteScore({ halfXPeople: SETTLEMENT.sitePeople, halfZPeople: SETTLEMENT.sitePeople });
  const rng = new SeededRandom(`${planet.id}-settlements`);
  const phase = rng.range(0, 2 * Math.PI), tilt = rng.range(-1, 1);
  // the scan: a Fibonacci sphere, its phase and its pole keyed by the planet, each direction scored
  const n = SETTLEMENT.candidates;
  const scored: Array<{ dir: Vec3; score: number }> = [];
  const ct = Math.sqrt(1 - tilt * tilt);
  for (let k = 0; k < n; k++) {
    const y = 1 - (2 * (k + 0.5)) / n, r = Math.sqrt(1 - y * y), a = k * 2.399963229728653 + phase;
    const x0 = r * dcos(a), z0 = r * dsin(a);
    // the pole turned about x by the keyed tilt (so no planet's sites sit on the axis the scan started from)
    const dir = { x: x0, y: y * ct - z0 * tilt, z: y * tilt + z0 * ct };
    scored.push({ dir, score: score(scoreState, planetIndex, dir) });
  }
  scored.sort((a, b) => b.score - a.score || a.dir.x - b.dir.x || a.dir.y - b.dir.y || a.dir.z - b.dir.z);
  // the best, spaced
  const spacing = (SETTLEMENT.spacingPeople * person) / planet.radius;
  const area = 4 * Math.PI * (planet.radius / person) * (planet.radius / person);
  const perPlanet = Math.max(SETTLEMENT.perPlanetMin, Math.min(SETTLEMENT.perPlanetMax, Math.round(area / (4 * SETTLEMENT.spacingPeople * SETTLEMENT.spacingPeople))));
  const sites: SettlementSite[] = [];
  for (const c of scored) {
    if (sites.length >= perPlanet) break;
    if (sites.some(s => arc(s.dir, c.dir) < spacing)) continue;
    const id = sites.length;
    const lots = settlementLots(planet, id, c.dir, person);
    sites.push({ id, dir: c.dir, yaw: lots.yaw, score: c.score, lots: lots.lots });
  }
  byKey.set(key, sites);
  return sites;
}

/** A settlement's lane and lots, keyed by the planet and the site: a yaw, 3–5 lots alternating either side of the lane, each a layout facing it. */
function settlementLots(planet: PlanetState, id: number, centre: Vec3, person: number): { yaw: number; lots: SettlementLot[] } {
  const rng = new SeededRandom(`${planet.id}-settlement-${id}`);
  const yaw = rng.range(0, 2 * Math.PI);
  const count = rng.int(SETTLEMENT.lotsMin, SETTLEMENT.lotsMax);
  const up = vecNormalize(centre), { tx, tz } = structureFrame({ yaw }, up);
  const lots: SettlementLot[] = [];
  for (let i = 0; i < count; i++) {
    const along = (i - (count - 1) / 2) * SETTLEMENT.pitchPeople * person, side = i % 2 === 0 ? 1 : -1;
    const off = side * SETTLEMENT.setbackPeople * person;
    const dir = vecNormalize({ x: up.x + (tx.x * along + tz.x * off) / planet.radius, y: up.y + (tx.y * along + tz.y * off) / planet.radius, z: up.z + (tx.z * along + tz.z * off) / planet.radius });
    // the door is on the layout's south face (its −z at no turn): a lot on the lane's +z side faces the lane at the site's yaw, one on the −z side turned about
    lots.push({ builder: rng.int(0, BUILDERS - 1), dir, yaw: side > 0 ? yaw : yaw + Math.PI });
  }
  return { yaw, lots };
}

/** The planets a match is fought on: where its people stand. */
function battlePlanets(state: Pick<GameState, 'players'>): number[] {
  return [...new Set(state.players.map(p => p.planetIndex))].sort((a, b) => a - b);
}

/**
 * The setup pass: the settlements the board bounds (with a board, those within SETTLEMENT.boardReach of the bases'
 * separation of each base; without one, the best), at most SETTLEMENT.maxPlaced, laid as neutral `house` structures —
 * one a lot, its builder the lot's layout, its yaw the lot's. The same state when the switch is off or nothing is laid.
 */
export function withSettlements(state: GameState): GameState {
  if (!state.config.settlements || !buildingsByFunction(state.config)) return state;
  const extra: StructureState[] = [];
  for (const planetIndex of battlePlanets(state)) {
    const sites = planetSettlements(state, planetIndex);
    if (!sites.length) continue;
    const bases = state.board?.bases.filter(b => b.planetIndex === planetIndex).map(b => vecNormalize(b.dir)) ?? [];
    let chosen = sites.slice();
    if (bases.length >= 2) {
      const sep = arc(bases[0], bases[1]);
      chosen = chosen.filter(s => bases.every(b => arc(s.dir, b) <= SETTLEMENT.boardReach * sep));
    }
    chosen.sort((a, b) => b.score - a.score || a.id - b.id);
    for (const s of chosen.slice(0, SETTLEMENT.maxPlaced)) for (const lot of s.lots) {
      extra.push({ id: state.structures.length + extra.length, type: 'house', planetIndex, surfaceDir: lot.dir, yaw: lot.yaw, health: structureHealth('house'), ownerId: -1, builder: lot.builder });
    }
  }
  return extra.length ? { ...state, structures: [...state.structures, ...extra] } : state;
}
